import { FormattedMenuSectionInfo, ItemModifier, MenuMappingItemDetails } from 'api'

type FilteredResults = {
  filteredItems: FormattedMenuSectionInfo[]
  filteredModifierGroups: { [id: MenuMappingItemDetails['id']]: FormattedMenuSectionInfo[] } // map each (parent or child) item
  filteredModifiers: { [id: FormattedMenuSectionInfo['id']]: FormattedMenuSectionInfo[] } // map each modifier-group → the modifiers that matched
}

/**
 * Collect every modifier-group inside `customizations` whose modifiers—
 * or nested modifiers—are in `updatedModifierIds`.  
 * Side-effect: fills `filteredModifiers`.
 * Returns the array of matching groups.
 */
function collectMatchingModifierGroups(
  customizations: FormattedMenuSectionInfo[] | undefined,
  updatedModifierIds: ItemModifier['id'][],
  filteredModifierIds: FormattedMenuSectionInfo['id'][],
  filteredModifiers: Record<
    FormattedMenuSectionInfo['id'],
    FormattedMenuSectionInfo[]
  >,
): FormattedMenuSectionInfo[] {
  const matchingItemModifierGroups: FormattedMenuSectionInfo[] = [];

  // drill into the item, modifier group and modifier to check if there is a keyword match.
  customizations?.forEach((customization) => {
    const matchingItemModifiers: FormattedMenuSectionInfo[] = [];

    // possible pre-existing modifier filter
    const customizationOptions = filteredModifierIds.length
      ? customization.children.customization_options?.filter((opt) =>
          filteredModifierIds.includes(opt.id),
        )
      : customization.children.customization_options;

    customizationOptions?.forEach((option) => {
     // direct match
      if (updatedModifierIds.includes(option.id)) {
        matchingItemModifiers.push(option);
      }

      const matchingNestedMods: FormattedMenuSectionInfo[] = [];
      
      // NESTED GROUPS & MODS
      option.children.customizations?.forEach((nestedCustomization) => {
        nestedCustomization.children.customization_options?.forEach(
          (nestedMod) => {
            if (updatedModifierIds.includes(nestedMod.id)) {
              matchingNestedMods.push(nestedMod);
            }
          },
        );

        if (matchingNestedMods.length) {
          matchingItemModifierGroups.push(nestedCustomization);
          filteredModifiers[nestedCustomization.id] = matchingNestedMods;
        }
      });
    });

    if (matchingItemModifiers.length) {
      matchingItemModifierGroups.push(customization);
      filteredModifiers[customization.id] = matchingItemModifiers;
    }
  });

  return matchingItemModifierGroups;
}

// In 86ing Preview, we want to show all items and modifiers that have changed
// If a modifier was updated and not the item, we want to still show the item with its modifier group and modifier
const get86ingPreviewFilteredItemsModifiers = (
  items: FormattedMenuSectionInfo[],
  updatedItemIds: MenuMappingItemDetails['id'][],
  updatedModifierIds: ItemModifier['id'][],
  filteredModifierIds: FormattedMenuSectionInfo['id'][] = [], // modifiers that were already filtered out
): FilteredResults => {
  const filteredItems: FormattedMenuSectionInfo[] = [];
  const filteredModifiers: { [id: FormattedMenuSectionInfo['id']]: FormattedMenuSectionInfo[] } = {} // map modifiers to their items
  const filteredModifierGroups: { [id: MenuMappingItemDetails['id']]: FormattedMenuSectionInfo[] } =
    {}

  items.forEach((item) => {
    // Top-level Item customizations
    const matchingItemModifierGroups: FormattedMenuSectionInfo[] = collectMatchingModifierGroups(
      item.children.customizations,
      updatedModifierIds,
      filteredModifierIds,
      filteredModifiers,
    );

    if (matchingItemModifierGroups.length) {
      filteredModifierGroups[item.id] = matchingItemModifierGroups;
    }

    // Child-items Level modifiers
    const matchingChildItemModifiers: FormattedMenuSectionInfo[] = []
    item.children.child_items?.forEach((child) => {
      const childMatchingGroups = collectMatchingModifierGroups(
        child.children.customizations,
        updatedModifierIds,
        filteredModifierIds,
        filteredModifiers,
      );

      if (childMatchingGroups.length) {
        filteredModifierGroups[child.id] = childMatchingGroups;
      }

      const includeChild =
        updatedItemIds.includes(child.id) || childMatchingGroups.length > 0;

      if (includeChild) {
        matchingChildItemModifiers.push(child);
      }
    });
    
    // Decide whether the item should appear in the preview
    const includeItem =
      matchingItemModifierGroups.length > 0 || // also add the item even if it doesn't match, but it's modifiers do
      matchingChildItemModifiers.length > 0 || // some of its children were updated
      updatedItemIds.includes(item.id) //  if the item itself matches the keyword, show the item.

    if (includeItem) {
      /* Push the item – trim child_items if only some were modified */
      filteredItems.push(
        matchingChildItemModifiers.length
          ? {
              ...item,
              children: {
                ...item.children,
                child_items: matchingChildItemModifiers,
              },
            }
          : item,
      );
    }

  })

  return { filteredItems, filteredModifierGroups, filteredModifiers }
}

export default get86ingPreviewFilteredItemsModifiers
